import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useIsDemo } from "@/hooks/common/useIsDemo";
import { PATH_ROOT } from "@/routes/paths";
import type {
  ContractIntelligenceContract,
  ContractIntelligenceView,
} from "@/types/contractIntelligence.types";

import { ContractIntelligenceDashboard } from "./ContractIntelligenceDashboard";
import { ContractIntelligenceEditor } from "./ContractIntelligenceEditor";
import { ContractIntelligenceUpload } from "./ContractIntelligenceUpload";
import { ContractTemplateGenerator } from "./templates/ContractTemplateGenerator";
import { ContractTemplatePicker } from "./templates/ContractTemplatePicker";

export default function ContractIntelligencePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isDemo = useIsDemo();
  const [contract, setContract] = useState<ContractIntelligenceContract | null>(null);
  const [view, setView] = useState<ContractIntelligenceView>("upload");

  useEffect(() => {
    if (isDemo && searchParams.get("view") === "template-library") {
      setView("template-library");
    }
  }, [isDemo, searchParams]);

  const comparisonPath = isDemo
    ? PATH_ROOT.demo.dealComparison
    : PATH_ROOT.main.comparison;

  const handleGenerate = () => {
    if (isDemo) {
      setView("template-library");
      return;
    }
    setView("generate");
  };

  const handleLoad = (loaded: ContractIntelligenceContract) => {
    setContract(loaded);
    setView("dashboard");
  };

  const handleClose = () => {
    setContract(null);
    setView("upload");
  };

  if (view === "generate") {
    return (
      <ContractTemplateGenerator
        onCancel={() => setView(contract ? "dashboard" : "upload")}
        onUploadExisting={() => setView("upload")}
        onComplete={(generated) => {
          setContract(generated);
          setView("editor");
        }}
      />
    );
  }

  if (view === "template-library") {
    return (
      <ContractTemplatePicker
        onCancel={() => setView("upload")}
        onUploadExisting={() => setView("upload")}
        onPick={(template) => {
          navigate(
            `${PATH_ROOT.demo.newDeal}?method=template&from=contract-iq&templateId=${encodeURIComponent(template.id)}`,
          );
        }}
      />
    );
  }

  if (!contract) {
    return (
      <ContractIntelligenceUpload
        onLoad={handleLoad}
        onGenerate={handleGenerate}
        onCompare={() => navigate(comparisonPath)}
      />
    );
  }

  if (view === "editor") {
    return (
      <ContractIntelligenceEditor contract={contract} onBackToDash={() => setView("dashboard")} />
    );
  }

  return (
    <ContractIntelligenceDashboard
      contract={contract}
      onOpenEditor={() => setView("editor")}
      onClose={handleClose}
    />
  );
}
